import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import './landing.css';
import { verifyContactLead, submitContactLead } from './utils/leadsAPI';
import {
  verifyAcademyContactLead,
  submitAcademyContactLead,
} from './utils/academyContactAPI';
import {
  registerPersonalAreaUser,
  sendPersonalAreaVerification,
  verifyPersonalAreaCode,
  refreshPersonalAreaSession,
} from './utils/personalAreaAPI';
import {
  clearPersonalAreaSession,
  readPersonalAreaSession,
  savePersonalAreaSession,
} from './utils/personalAreaSession';
import { personalAreaNavButtonLabel } from './utils/personalAreaNavLabel';
import { trackSiteEvent } from './utils/siteAnalytics';
import { buildInternationalPhone } from './utils/phone';
import PersonalAreaPage from './components/PersonalAreaPage';
import PersonalAreaOnboarding from './components/PersonalAreaOnboarding';
import WheelTimePicker from './components/WheelTimePicker';
import drMosheKatan from './assets/images/dr-moshe-katan.png';
import poznanUniversity from './assets/images/poznan-university-of-technology.png';
import cocaCola from './assets/images/kokakola.png';
import carlsberg from './assets/images/Carlsberg-Emblem.png';
import strauss from './assets/images/shtraus.png';
import tauLogo from './assets/images/tau4.png';
import collerLogo from './assets/images/Coller_Heb_Horiz_1.png';
import phdCertificate from './assets/images/תואר שלישי PhD-1.png';
import article1Image from './assets/images/article1.jpeg';
import StudentRecommendations from './components/StudentRecommendations';
import PhonePreview from './components/PhonePreview';
import OrganizationalCulture from './components/OrganizationalCulture';
import CoursesCatalogPage from './components/CoursesCatalogPage';
import CourseOfferCard, { LockedCourseCard } from './components/CourseOfferCard';
import CourseLandingPage from './components/CourseLandingPage';
import {
  getCatalogByView,
  ACADEMY_COURSE_CATALOGS,
  catalogCourseEntries,
} from './content/academyCourses';

const ACADEMY_DIGITAL_COURSES = catalogCourseEntries(ACADEMY_COURSE_CATALOGS.digital);

const BELIEF_VALUE_ITEMS = [
  { id: 'joy', label: 'שמחה' },
  { id: 'respect', label: 'כבוד' },
  { id: 'positive', label: 'חשיבה חיובית' },
  { id: 'growth', label: 'התפתחות מתמדת' },
];

function BeliefValueIcon({ id }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };

  switch (id) {
    case 'joy':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      );
    case 'respect':
      return (
        <svg {...common}>
          <path d="M12 3l2.2 4.5 4.9.7-3.5 3.4.8 4.9L12 14.8 7.6 16.5l.8-4.9L5 8.2l4.9-.7L12 3z" />
        </svg>
      );
    case 'positive':
      return (
        <svg {...common}>
          <path d="M12 3v3M12 18v3M5.5 5.5l2.1 2.1M16.4 16.4l2.1 2.1M3 12h3M18 12h3M5.5 18.5l2.1-2.1M16.4 7.6l2.1-2.1" />
          <circle cx="12" cy="12" r="3.5" />
        </svg>
      );
    case 'growth':
      return (
        <svg {...common}>
          <path d="M4 18h16M7 15l4-8 4 5 3-4" />
          <path d="M17 8h3v3" />
        </svg>
      );
    default:
      return null;
  }
}

function BeliefMissionVisionValues() {
  return (
    <>
      <div className="gl-belief-points">
        <article className="gl-belief-card gl-belief-card--purpose">
          <span className="gl-belief-card__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </span>
          <h3>היעוד</h3>
          <p>להנגיש כלים פרקטיים ואיכותיים, כדי שכל אדם יוכל ליישם אותם ביומיום ולהפוך למנהיג שמפתח אחרים סביבו.</p>
        </article>
        <article className="gl-belief-card gl-belief-card--vision">
          <span className="gl-belief-card__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
              <circle cx="12" cy="12" r="2.5" />
            </svg>
          </span>
          <h3>החזון</h3>
          <p>יותר אנשים עם כלים של מנהיגות: בני זוג טובים יותר, הורים טובים יותר, מנהלים טובים יותר, וחברה חזקה יותר.</p>
        </article>
      </div>
      <article className="gl-belief-values-card">
        <div className="gl-belief-values-card__head">
          <span className="gl-belief-values-card__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 21s-6-4.2-6-10a4 4 0 0 1 8 0 4 4 0 0 1 8 0c0 5.8-6 10-6 10z" />
            </svg>
          </span>
          <h3>הערכים שלנו</h3>
        </div>
        <ul className="gl-values gl-values--cards">
          {BELIEF_VALUE_ITEMS.map((item) => (
            <li
              key={item.id}
              className={`gl-value-card gl-value-card--${item.id}`}
            >
              <span className="gl-value-card__icon" aria-hidden="true">
                <BeliefValueIcon id={item.id} />
              </span>
              <span className="gl-value-card__label">{item.label}</span>
            </li>
          ))}
        </ul>
      </article>
    </>
  );
}

// Member Home Component - REMOVED

function StoreButtons() {
  return (
    <div className="download-buttons-container">
      <a
        href="https://apps.apple.com/app/id6448213401"
        className="store-badge apple-badge"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackSiteEvent('store_click', { path: '/store/app-store', title: 'App Store' })}
      >
        <div className="badge-content">
          <div className="apple-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01M12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
          </div>
          <div className="badge-text">
            <span className="badge-label">הורד ב</span>
            <span className="badge-store">App Store</span>
          </div>
        </div>
      </a>
      <a
        href="https://play.google.com/store/apps/details?id=com.greatleaders.mobile"
        className="store-badge google-badge"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackSiteEvent('store_click', { path: '/store/google-play', title: 'Google Play' })}
      >
        <div className="badge-content">
          <div className="google-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
            </svg>
          </div>
          <div className="badge-text">
            <span className="badge-label">זמין ב</span>
            <span className="badge-store">Google Play</span>
          </div>
        </div>
      </a>
    </div>
  );
}

const ROOT_ROUTE_ENABLED =
  process.env.REACT_APP_ENABLE_ROOT_ROUTE === 'true' ||
  process.env.NODE_ENV === 'development';
const DEFAULT_SITE_PATH = ROOT_ROUTE_ENABLED ? '/' : '/academy';

const SITE_ROUTES = {
  '/academy': 'main',
  '/dev': 'dev',
  '/test': 'test',
};

if (ROOT_ROUTE_ENABLED) {
  SITE_ROUTES['/'] = 'main';
}

function readSitePathname() {
  return window.location.pathname.replace(/\/+$/, '') || '/';
}

function readSiteRoute() {
  const path = readSitePathname();
  return SITE_ROUTES[path] || null;
}

function App() {
  const [siteRoute, setSiteRoute] = useState(() => readSiteRoute() || 'main');
  const [sitePath, setSitePath] = useState(() => readSitePathname());
  const isAcademySite = sitePath === '/academy';
  const brandHref =
    ROOT_ROUTE_ENABLED && sitePath !== '/' ? '/' : '#top';
  const [currentView, setCurrentView] = useState('main');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseListReturnView, setCourseListReturnView] = useState(null);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [showTalkPopup, setShowTalkPopup] = useState(false);
  const [showPersonalAreaPopup, setShowPersonalAreaPopup] = useState(false);
  const [personalAreaPhone, setPersonalAreaPhone] = useState('');
  const [personalAreaCountryCode, setPersonalAreaCountryCode] = useState('+972');
  const [personalAreaUser, setPersonalAreaUser] = useState(() => readPersonalAreaSession());
  const [personalAreaVerifiedPhone, setPersonalAreaVerifiedPhone] = useState('');
  const [personalAreaOnboardingStep, setPersonalAreaOnboardingStep] = useState(null);
  const [onboardingName, setOnboardingName] = useState('');
  const [onboardingGender, setOnboardingGender] = useState('');
  const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(false);
  const [acceptedTermsOfUse, setAcceptedTermsOfUse] = useState(false);
  const [isSubmittingPersonalArea, setIsSubmittingPersonalArea] = useState(false);
  const [isRegisteringPersonalArea, setIsRegisteringPersonalArea] = useState(false);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [verificationFlow, setVerificationFlow] = useState('contact');
  const [verificationCode, setVerificationCode] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [successPopup, setSuccessPopup] = useState(null); // { name, purpose }
  const codeInputRefs = useRef([]);
  const [contactForm, setContactForm] = useState({
    fullName: '',
    contactMethod: 'phone',
    phone: '',
    countryCode: '+972', // Default to Israel
    email: '',
    availableTimes: [{ date: '', fromTime: '', toTime: '' }]
  });
  const countryCodeOptions = [
    { value: '+972', label: '🇮🇱 ישראל (+972)' },
    { value: '+1', label: '🇺🇸 ארה"ב (+1)' },
    { value: '+44', label: '🇬🇧 בריטניה (+44)' },
    { value: '+49', label: '🇩🇪 גרמניה (+49)' },
    { value: '+33', label: '🇫🇷 צרפת (+33)' },
    { value: '+39', label: '🇮🇹 איטליה (+39)' },
    { value: '+34', label: '🇪🇸 ספרד (+34)' },
    { value: '+31', label: '🇳🇱 הולנד (+31)' },
    { value: '+41', label: '🇨🇭 שוויץ (+41)' },
    { value: '+43', label: '🇦🇹 אוסטריה (+43)' },
    { value: '+32', label: '🇧🇪 בלגיה (+32)' },
    { value: '+46', label: '🇸🇪 שוודיה (+46)' },
    { value: '+47', label: '🇳🇴 נורווגיה (+47)' },
    { value: '+45', label: '🇩🇰 דנמרק (+45)' },
    { value: '+358', label: '🇫🇮 פינלנד (+358)' },
    { value: '+7', label: '🇷🇺 רוסיה (+7)' },
    { value: '+86', label: '🇨🇳 סין (+86)' },
    { value: '+81', label: '🇯🇵 יפן (+81)' },
    { value: '+82', label: '🇰🇷 דרום קוריאה (+82)' },
    { value: '+91', label: '🇮🇳 הודו (+91)' },
    { value: '+61', label: '🇦🇺 אוסטרליה (+61)' },
    { value: '+64', label: '🇳🇿 ניו זילנד (+64)' },
    { value: '+27', label: '🇿🇦 דרום אפריקה (+27)' },
    { value: '+971', label: '🇦🇪 איחוד האמירויות (+971)' },
    { value: '+966', label: '🇸🇦 ערב הסעודית (+966)' },
    { value: '+20', label: '🇪🇬 מצרים (+20)' },
    { value: '+90', label: '🇹🇷 טורקיה (+90)' },
    { value: '+351', label: '🇵🇹 פורטוגל (+351)' },
    { value: '+353', label: '🇮🇪 אירלנד (+353)' },
    { value: '+48', label: '🇵🇱 פולין (+48)' },
    { value: '+420', label: '🇨🇿 צ\'כיה (+420)' },
    { value: '+36', label: '🇭🇺 הונגריה (+36)' },
    { value: '+40', label: '🇷🇴 רומניה (+40)' },
    { value: '+30', label: '🇬🇷 יוון (+30)' }
  ];

  useEffect(() => {
    const applyRoute = () => {
      setSitePath(readSitePathname());
      const route = readSiteRoute();
      if (!route) {
        window.history.replaceState(null, '', DEFAULT_SITE_PATH);
        setSiteRoute('main');
        setSitePath(readSitePathname());
        return;
      }
      setSiteRoute(route);
    };
    applyRoute();
    window.addEventListener('popstate', applyRoute);
    return () => window.removeEventListener('popstate', applyRoute);
  }, []);

  const environment = siteRoute === 'dev' || siteRoute === 'test' ? siteRoute : 'prod';

  useEffect(() => {
    const path =
      currentView === 'course-landing'
        ? '/course'
        : currentView === 'pricing'
          ? '/pricing'
          : currentView === 'courses-digital'
            ? '/courses/digital'
            : currentView === 'personal-area'
              ? '/personal'
              : '/';
    const title =
      currentView === 'course-landing'
        ? 'דף קורס'
        : currentView === 'pricing'
          ? 'מחירים'
          : currentView === 'courses-digital'
            ? 'קורסים דיגיטליים'
            : currentView === 'personal-area'
              ? 'איזור אישי'
              : 'דף הבית';
    trackSiteEvent('pageview', { environment, path, title });
  }, [environment, currentView]);

  useEffect(() => {
    if (currentView === 'main') return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentView]);

  const goToMain = () => {
    setCourseListReturnView(null);
    setCurrentView('main');
  };

  const openCoursesCatalog = (view) => {
    setCourseListReturnView(null);
    setCurrentView(view);
  };

  const openCourseFromCatalog = (course) => {
    setCourseListReturnView(currentView);
    setSelectedCourse(course);
    setCurrentView('course-landing');
  };

  const handleCourseLandingBack = () => {
    const returnView = courseListReturnView || 'main';
    setCourseListReturnView(null);
    setCurrentView(returnView);
  };

  const activeCatalog = getCatalogByView(currentView);

  useEffect(() => {
    if (currentView !== 'main') return undefined;
    const seen = new Set();
    const nodes = ['app', 'stories', 'founder', 'download']
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!nodes.length) return undefined;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.4) return;
        const id = entry.target.id;
        if (!id || seen.has(id)) return;
        seen.add(id);
        trackSiteEvent('section', { environment, path: `/#${id}`, title: id });
      });
    }, { threshold: [0.4] });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [environment, currentView]);

  const contactAnalyticsPath = isAcademySite ? '/academy/contact' : '/contact';

  const openTalk = () => {
    trackSiteEvent('contact_start', { environment, path: contactAnalyticsPath, title: 'צור קשר' });
    setShowTalkPopup(true);
  };

  const enterPersonalArea = (user) => {
    savePersonalAreaSession(user);
    setPersonalAreaUser(user);
    setCurrentView('personal-area');
    trackSiteEvent('personal_area_enter', {
      environment,
      path: '/personal',
      title: 'איזור אישי',
    });
  };

  const signOutPersonalArea = useCallback(() => {
    clearPersonalAreaSession();
    setPersonalAreaUser(null);
    setCurrentView((view) => (view === 'personal-area' ? 'main' : view));
  }, []);

  useEffect(() => {
    const userId = personalAreaUser?.id;
    const phone = personalAreaUser?.phone;
    if (!userId || !phone) return undefined;

    let cancelled = false;

    const syncSession = async () => {
      try {
        const result = await refreshPersonalAreaSession({ userId, phone });
        if (cancelled) return;
        const user = result.data?.user;
        if (user?.id) {
          savePersonalAreaSession(user);
          setPersonalAreaUser(user);
        }
      } catch (error) {
        if (cancelled) return;
        if (error?.code === 'PERSONAL_AREA_SESSION_ENDED') {
          signOutPersonalArea();
        }
      }
    };

    syncSession();
    const intervalId = window.setInterval(syncSession, 5000);
    const onFocus = () => {
      syncSession();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [personalAreaUser?.id, personalAreaUser?.phone, signOutPersonalArea]);

  const resetPersonalAreaOnboarding = () => {
    setPersonalAreaOnboardingStep(null);
    setOnboardingName('');
    setOnboardingGender('');
    setAcceptedPrivacyPolicy(false);
    setAcceptedTermsOfUse(false);
    setPersonalAreaVerifiedPhone('');
  };

  const openPersonalAreaPopup = () => {
    trackSiteEvent('personal_area_open', { environment, path: '/personal', title: 'איזור אישי' });
    const session = readPersonalAreaSession();
    if (session) {
      enterPersonalArea(session);
      return;
    }
    setShowPersonalAreaPopup(true);
  };

  const closePersonalAreaPopup = () => {
    setShowPersonalAreaPopup(false);
  };

  const handlePersonalAreaSubmit = async (event) => {
    event.preventDefault();
    if (isSubmittingPersonalArea) return;
    const phone = buildInternationalPhone(personalAreaCountryCode, personalAreaPhone);
    if (!phone) return;
    setIsSubmittingPersonalArea(true);
    trackSiteEvent('personal_area_send', { environment, path: '/personal', title: 'איזור אישי' });
    try {
      const result = await sendPersonalAreaVerification(phone);
      setPersonalAreaVerifiedPhone(phone);
      setVerificationCode(result.data?.verificationCode || '');
      setVerificationFlow('personal');
      setShowPersonalAreaPopup(false);
      setShowVerificationScreen(true);
    } catch (error) {
      console.error('Error sending personal area verification:', error);
      alert(`❌ ${error.message || 'אופס! משהו השתבש בשליחת קוד האימות. אנא נסה שנית.'}`);
    } finally {
      setIsSubmittingPersonalArea(false);
    }
  };

  useEffect(() => {
    if (!showTalkPopup && !showPersonalAreaPopup && !personalAreaOnboardingStep) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (showTalkPopup) setShowTalkPopup(false);
      if (showPersonalAreaPopup) setShowPersonalAreaPopup(false);
      if (personalAreaOnboardingStep) resetPersonalAreaOnboarding();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [showTalkPopup, showPersonalAreaPopup, personalAreaOnboardingStep]);

  useEffect(() => {
    if (showVerificationScreen && codeInputRefs.current[0]) {
      setTimeout(() => {
        codeInputRefs.current[0]?.focus();
      }, 100);
    }
  }, [showVerificationScreen]);

  // Course and lecture data
  const handleContactFormChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const activeVerificationForm =
    verificationFlow === 'personal'
      ? {
          contactMethod: 'phone',
          countryCode: personalAreaCountryCode,
          phone: personalAreaPhone,
        }
      : contactForm;

  const clearVerificationState = () => {
    setShowVerificationScreen(false);
    setVerificationCode('');
    setEnteredCode('');
    setVerificationFlow('contact');
  };

  const closeVerificationScreen = () => {
    const wasPersonal = verificationFlow === 'personal';
    clearVerificationState();
    if (wasPersonal) setShowPersonalAreaPopup(true);
  };

  const openSuccessPopup = ({ name, purpose }) => {
    clearVerificationState();
    setShowTalkPopup(false);
    setSuccessPopup({
      name: String(name || '').trim() || 'אורח/ת',
      purpose,
    });
  };

  const closeSuccessPopup = () => {
    setSuccessPopup(null);
  };

  const addTimeSlot = () => {
    setContactForm(prev => ({
      ...prev,
      availableTimes: [...prev.availableTimes, { date: '', fromTime: '', toTime: '' }]
    }));
  };

  const updateTimeSlot = (index, field, value) => {
    setContactForm((prev) => ({
      ...prev,
      availableTimes: prev.availableTimes.map((timeSlot, i) => {
        if (i !== index) return timeSlot;
        const next = { ...timeSlot, [field]: value };
        if (field === 'fromTime' && next.toTime && value && next.toTime < value) {
          next.toTime = '';
        }
        return next;
      }),
    }));
  };

  const removeTimeSlot = (index) => {
    setContactForm(prev => ({
      ...prev,
      availableTimes: prev.availableTimes.filter((_, i) => i !== index)
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmittingContact) return;
    
    setIsSubmittingContact(true);
    
    try {
      // First, send verification code
      const phone = contactForm.contactMethod === 'phone' 
        ? buildInternationalPhone(contactForm.countryCode, contactForm.phone)
        : undefined;
      const email = contactForm.contactMethod === 'email' 
        ? contactForm.email?.trim() 
        : undefined;

      const verifyData = {
        preferredContactMethod: contactForm.contactMethod,
        phone: phone,
        email: email
      };

      const verifyResult = isAcademySite
        ? await verifyAcademyContactLead(verifyData)
        : await verifyContactLead(verifyData);

      if (verifyResult.success) {
        setVerificationCode(verifyResult.data.verificationCode);
        setVerificationFlow('contact');
        setShowVerificationScreen(true);
        setIsSubmittingContact(false);
      } else {
        throw new Error(verifyResult.message || 'שגיאה בשליחת קוד האימות');
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      alert(`❌ ${error.message || 'אופס! משהו השתבש בשליחת קוד האימות. אנא נסה שנית.'}`);
      setIsSubmittingContact(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();

    if (isVerifying) return;

    setIsVerifying(true);

    try {
      if (verificationFlow === 'personal') {
        const result = await verifyPersonalAreaCode({
          phone: personalAreaVerifiedPhone,
          code: enteredCode.trim(),
        });
        setPersonalAreaPhone('');
        setPersonalAreaCountryCode('+972');
        clearVerificationState();
        if (result.data?.registered && result.data?.user) {
          enterPersonalArea(result.data.user);
        } else {
          setPersonalAreaOnboardingStep('name');
        }
        return;
      }

      const formattedTimes = contactForm.availableTimes
        .filter((timeSlot) => timeSlot.date && timeSlot.fromTime)
        .map((timeSlot) => {
          const fromTimeTrimmed = timeSlot.fromTime.trim();
          const toTimeTrimmed = timeSlot.toTime?.trim();
          const toTime =
            toTimeTrimmed && toTimeTrimmed !== fromTimeTrimmed
              ? toTimeTrimmed
              : fromTimeTrimmed;

          return {
            date: timeSlot.date,
            fromTime: fromTimeTrimmed,
            toTime,
          };
        });

      const result = isAcademySite
        ? await submitAcademyContactLead({
            fullName: contactForm.fullName.trim(),
            preferredContactMethod: contactForm.contactMethod,
            phone: contactForm.phone,
            countryCode: contactForm.countryCode,
            email: contactForm.contactMethod === 'email'
              ? contactForm.email?.trim()
              : undefined,
            availableTimes: formattedTimes,
            verificationCode: enteredCode.trim(),
          })
        : await (async () => {
            if (enteredCode.trim() !== verificationCode) {
              alert('❌ קוד האימות שגוי. אנא נסה שנית.');
              setEnteredCode('');
              setIsVerifying(false);
              return { success: false };
            }

            const contactData = {
              fullName: contactForm.fullName.trim(),
              preferredContactMethod: contactForm.contactMethod,
              phone: contactForm.contactMethod === 'phone'
                ? buildInternationalPhone(contactForm.countryCode, contactForm.phone)
                : undefined,
              email: contactForm.contactMethod === 'email'
                ? contactForm.email?.trim()
                : undefined,
              availableTimes: formattedTimes,
              environment,
              siteEntry: 'main',
            };

            return submitContactLead(contactData);
          })();

      if (!result?.success) {
        return;
      }

      if (result.success) {
        trackSiteEvent('contact_submit', { environment, path: contactAnalyticsPath, title: 'פנייה' });
        const name = contactForm.fullName.trim();
        setContactForm({
          fullName: '',
          contactMethod: 'phone',
          phone: '',
          countryCode: '+972',
          email: '',
          availableTimes: [{ date: '', fromTime: '', toTime: '' }],
        });
        openSuccessPopup({ name, purpose: 'talk' });
      } else {
        throw new Error(result.message || 'שגיאה בשליחת הטופס');
      }
    } catch (error) {
      console.error('Error submitting verified form:', error);
      alert(`❌ ${error.message || 'אופס! משהו השתבש. אנא נסה שנית או צור קשר ישירות.'}`);
    } finally {
      setIsVerifying(false);
    }
  };

  // Removed: posts state variable
  // Removed: newPost, showArticle, currentArticle, currentLesson state variables

  // Removed: egoArticle, dailyRoutineArticle, holisticLeadershipArticle
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organizationType: '',
    organizationName: '',
    serviceType: '',
    specificService: '',
    message: ''
  });

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // כאן תוכל להוסיף לוגיקה לשליחת הטופס
    alert('תודה על הפנייה! נחזור אליך בהקדם.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          organizationType: '',
          organizationName: '',
          serviceType: '',
          specificService: '',
          message: ''
        });
  };

  // Removed: handleSubscribe, handlePaymentComplete, handlePayPalPayment

  // Removed: toggleComments, handlePostSubmit functions


  // Scroll Animation Effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    // Observe all elements with scroll animation classes
    const animatedElements = document.querySelectorAll('.scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale');
    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Checkout Screen Component - REMOVED
  /*const CheckoutScreen = () => (
    <div className="checkout-screen">
      <div className="container">
        <button 
          className="back-button"
          onClick={() => setCurrentView('pricing')}
        >
          חזרה
          <span className="back-icon">←</span>
        </button>
        
        <div className="checkout-content">
          <div className="checkout-card">
            <div className="order-summary">
              <h2>סיכום הזמנה</h2>
              <div className="order-item">
                <span className="item-name">מנוי חודשי לקהילת המנהיגים</span>
                <span className="item-price">99 ₪</span>
              </div>
              <div className="order-total">
                <span className="total-label">סה"כ לתשלום:</span>
                <span className="total-price">99 ₪</span>
              </div>
            </div>
            
            <div className="payment-form">
              <h3>פרטי תשלום</h3>
              <div className="form-group">
                <input type="text" placeholder="שם מלא" />
              </div>
              <div className="form-group">
                <input type="email" placeholder="כתובת אימייל" />
              </div>
              <div className="form-group">
                <input type="password" placeholder="סיסמה" />
              </div>
              <div className="form-group">
                <input type="tel" placeholder="מספר טלפון" />
              </div>
              <div className="form-group">
                <input type="text" placeholder="מספר כרטיס אשראי" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input type="text" placeholder="תאריך תפוגה (MM/YY)" />
                </div>
                <div className="form-group">
                  <input type="text" placeholder="CVV" />
                </div>
              </div>
              
              <button className="pay-button" onClick={handlePayPalPayment}>
                <span className="pay-text">שלם עם PayPal</span>
                <span className="pay-amount">99 ₪</span>
              </button>
              
              <div className="security-badges">
                <span className="security-text">🔒 תשלום מאובטח</span>
                <span className="security-text">🛡️ מוגן SSL</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );*/

  // Pricing Screen Component - REMOVED
  /*const PricingScreen = () => (
    <div className="pricing-screen">
      
      <div className="container">
        <button 
          className="back-button"
          onClick={() => setCurrentView('main')}
        >
          חזרה
          <span className="back-icon">←</span>
        </button>
        
        <div className="pricing-hero">
          <h1 className="pricing-title">
            <span className="title-main">הצטרפו לקהילת המנהיגים</span>
          </h1>
          <p className="pricing-subtitle">
            התחל את המסע שלך לפיתוח מנהיגות עם כלים מעשיים ותמיכה מלאה
          </p>
        </div>
        
        <div className="benefits-grid">
          <div className="benefit-card benefit-card-1">
            <div className="benefit-header">
              <div className="benefit-icon-wrapper">
                <span className="benefit-icon">📚</span>
              </div>
              <div className="benefit-number">01</div>
            </div>
            <div className="benefit-content">
              <h3 className="benefit-title">גישה לכל הקורסים הדיגיטליים</h3>
              <p className="benefit-description">
                קורסים מקיפים בפיתוח מנהיגות עם ד"ר משה קטן, כולל שיטת C.O.D הייחודית
              </p>
              <div className="benefit-features">
                <span className="feature-tag">+15 קורסים</span>
                <span className="feature-tag">תמיכה מלאה</span>
              </div>
            </div>
          </div>
          
          <div className="benefit-card benefit-card-2">
            <div className="benefit-header">
              <div className="benefit-icon-wrapper">
                <span className="benefit-icon">👥</span>
              </div>
              <div className="benefit-number">02</div>
            </div>
            <div className="benefit-content">
              <h3 className="benefit-title">חברות בקהילת המנהיגים</h3>
              <p className="benefit-description">
                שיתוף דילמות והתפתחות עם מנהיגים אחרים, קבלת ייעוץ ותמיכה מהקהילה
              </p>
              <div className="benefit-features">
                <span className="feature-tag">קהילה פעילה</span>
                <span className="feature-tag">ייעוץ אישי</span>
              </div>
            </div>
          </div>
          
          <div className="benefit-card benefit-card-3">
            <div className="benefit-header">
              <div className="benefit-icon-wrapper">
                <span className="benefit-icon">🎥</span>
              </div>
              <div className="benefit-number">03</div>
            </div>
            <div className="benefit-content">
              <h3 className="benefit-title">תוכן בלעדי</h3>
              <p className="benefit-description">
                וידאו, הרצאות ותכנים ייחודיים רק למנויים, כולל חומרים שלא זמינים במקום אחר
              </p>
              <div className="benefit-features">
                <span className="feature-tag">תוכן בלעדי</span>
                <span className="feature-tag">עדכונים שוטפים</span>
              </div>
            </div>
          </div>
          
          <div className="benefit-card benefit-card-4">
            <div className="benefit-header">
              <div className="benefit-icon-wrapper">
                <span className="benefit-icon">🎤</span>
              </div>
              <div className="benefit-number">04</div>
            </div>
            <div className="benefit-content">
              <h3 className="benefit-title">הרצאה חודשית לקהילה</h3>
              <p className="benefit-description">
                הרצאה חיה עם ד"ר משה קטן פעם בחודש, כולל שאלות ותשובות אישיות
              </p>
              <div className="benefit-features">
                <span className="feature-tag">הרצאה חיה</span>
                <span className="feature-tag">Q&A אישי</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pricing-section">
          <div className="pricing-card">
            <div className="pricing-header">
              <div className="pricing-badge">
                <span className="badge-text">מחיר מיוחד</span>
                <span className="badge-discount">-50%</span>
              </div>
              <h2 className="pricing-card-title">מנוי חודשי</h2>
            </div>
            
            <div className="price-display">
              <div className="price-amount">
                <span className="currency">₪</span>
                <span className="amount">99</span>
                <span className="period">לחודש</span>
              </div>
              <div className="price-details">
                <span className="original-price">₪198 לחודש</span>
                <span className="savings">חיסכון של ₪1,188 בשנה!</span>
              </div>
            </div>
            
            <div className="pricing-features">
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>גישה לכל הקורסים ללא הגבלה</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>חברות בקהילת המנהיגים</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>תוכן בלעדי ועדכונים שוטפים</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>הרצאה חודשית חיה</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>תמיכה טכנית 24/7</span>
              </div>
            </div>
            
            <button className="subscribe-button" onClick={handleSubscribe}>
              <span className="button-text">התחל עכשיו</span>
              <span className="button-price">99 ₪ לחודש</span>
              <div className="button-glow"></div>
            </button>
            
            <div className="guarantee">
              <span className="guarantee-icon">🛡️</span>
              <span className="guarantee-text">ערבות החזר כספי מלא למשך 30 יום</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );*/

  const handleOnboardingNameSubmit = (event) => {
    event.preventDefault();
    if (!onboardingName.trim()) return;
    setPersonalAreaOnboardingStep('gender');
  };

  const handleOnboardingGenderSubmit = (event) => {
    event.preventDefault();
    if (!onboardingGender) return;
    setPersonalAreaOnboardingStep('legal');
  };

  const handleOnboardingComplete = async (event) => {
    event.preventDefault();
    if (isRegisteringPersonalArea) return;
    if (!acceptedPrivacyPolicy || !acceptedTermsOfUse) return;
    setIsRegisteringPersonalArea(true);
    try {
      const result = await registerPersonalAreaUser({
        phone: personalAreaVerifiedPhone,
        name: onboardingName.trim(),
        gender: onboardingGender,
        acceptedPrivacyPolicy,
        acceptedTermsOfUse,
      });
      const user = result.data?.user;
      if (!user) throw new Error('שגיאה בהרשמה');
      resetPersonalAreaOnboarding();
      enterPersonalArea(user);
    } catch (error) {
      console.error('Personal area registration failed:', error);
      alert(`❌ ${error.message || 'לא הצלחנו להשלים ההרשמה. נסו שוב.'}`);
    } finally {
      setIsRegisteringPersonalArea(false);
    }
  };

  return (
    <div className="App" dir="rtl">
      {currentView === 'personal-area' ? (
        <PersonalAreaPage
          user={personalAreaUser}
          onBack={() => setCurrentView('main')}
          onSignOut={() => {
            signOutPersonalArea();
            setCurrentView('main');
          }}
          onOpenCourse={openCourseFromCatalog}
          onBrowseCatalog={() => setCurrentView('courses-digital')}
        />
      ) : currentView === 'course-landing' ? (
        <CourseLandingPage
          selectedCourse={selectedCourse}
          onBack={handleCourseLandingBack}
          personalAreaUser={personalAreaUser}
        />
      ) : activeCatalog ? (
        <CoursesCatalogPage
          catalog={activeCatalog}
          onBack={goToMain}
          onOpenCourse={openCourseFromCatalog}
        />
      ) : (
        <div
          className={`gl-home${isAcademySite ? ' gl-home--academy' : ''}`}
          data-site-route={siteRoute}
        >
          <div className="gl-top">
          {isAcademySite && (
            <>
              <div className="gl-academy-hero__orb gl-academy-hero__orb--gold" aria-hidden="true" />
              <div className="gl-academy-hero__orb gl-academy-hero__orb--blue" aria-hidden="true" />
            </>
          )}
          <header className="gl-nav">
            <a className="gl-brand" href={brandHref}>
              <img src="/phone/gl-logo.png" alt="" className="gl-brand-mark" />
              <span>Great Leaders</span>
            </a>
            <div className="gl-nav-actions">
              <button
                type="button"
                className={`gl-nav-cta gl-nav-personal${personalAreaUser ? ' gl-nav-personal--signed-in' : ''}`}
                onClick={openPersonalAreaPopup}
              >
                {personalAreaNavButtonLabel(personalAreaUser)}
              </button>
              <button
                type="button"
                className="gl-nav-contact"
                onClick={openTalk}
              >
                צור קשר
              </button>
            </div>
          </header>

          {isAcademySite && (
            <section className="gl-belief gl-academy-hero" id="top">
              <div className="gl-belief-quote">
                <h2>
                  כל אחד יכול להיות מנהיג
                  <span>בבית, בעבודה, ועם החברים</span>
                </h2>
              </div>
              <p className="gl-academy-hero-lead">
                מסלולי לימוד מעשיים לפיתוח מנהיגות — דיגיטלי בקצב שלכם.
              </p>
              <BeliefMissionVisionValues />
            </section>
          )}

          {!isAcademySite && (
            <section className="gl-hero" id="top">
              <div className="gl-hero-copy">
                <p className="gl-kicker">פיתוח מנהיגות לחיים עצמם</p>
                <h1>
                  כל אחד יכול להיות מנהיג
                  <span>האפליקציה הופכת את זה להרגל יומי</span>
                </h1>
                <p className="gl-hero-lead">
                  כלים פרקטיים לפיתוח מנהיגות, בקצב שלך, בבית, בעבודה ועם האנשים שסביבך.
                </p>
                <StoreButtons />
              </div>
              <div className="gl-hero-stage">
                <PhonePreview />
              </div>
            </section>
          )}
          </div>

          {!isAcademySite && (
            <section className="gl-proof" aria-label="הוכחות">
              <div>
                <strong>מעל 200</strong>
                <span>סטודנטים</span>
              </div>
              <div>
                <strong>6</strong>
                <span>קורסים באוניברסיטת תל אביב</span>
              </div>
              <div>
                <strong>3</strong>
                <span>תעודות הצטיינות של הדיקאן</span>
              </div>
            </section>
          )}

          <section id="app" className="gl-offer">
            <div className="gl-section-head gl-offer-head">
              <p className="gl-kicker">מה אנחנו מציעים</p>
              <h2>
                מגוון קורסים דיגיטליים לפיתוח מנהיגות
                <span>למידה בקצב שלכם, בכל מקום.</span>
              </h2>
            </div>
            <div className="gl-offer-grid">
              {ACADEMY_DIGITAL_COURSES.map(({ course, locked }, index) => (
                locked ? (
                  <LockedCourseCard key={course.id} course={course} index={index} />
                ) : (
                  <CourseOfferCard
                    key={course.id}
                    course={course}
                    index={index}
                    onOpen={openCourseFromCatalog}
                  />
                )
              ))}
            </div>
          </section>

          {!isAcademySite && <OrganizationalCulture />}

          {!isAcademySite && (
            <section className="gl-belief">
              <div className="gl-belief-quote">
                <p className="gl-kicker">אנחנו מאמינים</p>
                <h2>
                  כל אחד יכול להיות מנהיג
                  <span>בבית, בעבודה, ועם החברים</span>
                </h2>
              </div>
              <BeliefMissionVisionValues />
            </section>
          )}

      {/* Our Experience Section */}
      <section id="stories" className="experience-section scroll-animate gl-stories">
        <div className="container">
          <div className="experience-content gl-stories-content">
            <StudentRecommendations tauLogo={tauLogo} collerLogo={collerLogo} />
          </div>
        </div>
      </section>



      {/* Instructor Section */}
      <section id="founder" className="instructor-section scroll-animate gl-founder">
        <div className="container">
          <div className="instructor-content">
            <div className="instructor-image">
              <div className="instructor-portrait">
                <img 
                  src={drMosheKatan} 
                  alt="ד״ר משה קטן" 
                  className="dr-moshe-photo"
                />
              </div>
            </div>
            <div className="instructor-info">
              <h3>ד"ר משה קטן</h3>
              <div className="instructor-content-wrapper">
                <div className="certificate-section">
                  <div className="certificate-container">
                    <img src={phdCertificate} alt="תעודת דוקטורט מאוניברסיטת פוזנן" className="certificate-image" />
                  </div>
                </div>
                <div className="instructor-text-content">
                  <p className="instructor-description">
                    אחרי שביצע שורה של תפקידים בכירים והנהיג צוותים גדולים בחברות בינלאומיות הפועלות בישראל כמו: קרלסברג, קוקה קולה ושטראוס עלית, דר' קטן נמנה היום עם מס' מצומצם של מומחים לפיתוח מנהיגות עסקית מעשית. זאת באמצעות גילוי ה-C.O.D לפיתוח מנהיגות במסגרת לימודי הדוקטורט שלו ב-University of Poznan. בנוסף הוא Chair ב-Vistage ומוביל קבוצת מנכ״לים בישראל.
                  </p>
                  <p className="instructor-description">
                    שיטת ה- C.O.D התגלתה במהלך המחקר המעמיק שביצע עם מנהלים בכירים בחברות גלובליות, התהליך מאפשר כיום לאירגונים רבים לשפר משמעותית את תפוקות הארגון ולהתמודד מול שינויים מתכוננים ושינויים בלתי צפויים.
                  </p>
                </div>
              </div>
              <div className="instructor-achievements">
                <div className="achievement">
                  <span className="achievement-icon">🏆</span>
                  <span>מומחה לפיתוח מנהיגות עסקית מעשית</span>
                </div>
                <div className="achievement">
                  <span className="achievement-icon">⭐</span>
                  <span>גילוי שיטת C.O.D במחקר דוקטורט</span>
                </div>
                <div className="achievement">
                  <span className="achievement-icon">🎯</span>
                  <span>30 שנות ניסיון בחברות בינלאומיות</span>
                </div>
              </div>
              
              <div className="instructor-logos">
                <div className="logos-grid">
                  <div className="logo-item">
                    <div className="logo-placeholder university">
                      <img src={poznanUniversity} alt="University of Poznan" className="logo-image" />
                    </div>
                  </div>
                  <div className="logo-item">
                    <div className="logo-placeholder company">
                      <img src={cocaCola} alt="Coca-Cola" className="logo-image" />
                    </div>
                  </div>
                  <div className="logo-item">
                    <div className="logo-placeholder company">
                      <img src={carlsberg} alt="Carlsberg" className="logo-image" />
                    </div>
                  </div>
                  <div className="logo-item">
                    <div className="logo-placeholder company">
                      <img src={strauss} alt="Strauss Elite" className="logo-image" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      <section
        id="download"
        className={`gl-download${isAcademySite ? ' gl-download--academy' : ''}`}
      >
        <div className="gl-download-inner">
          {isAcademySite ? (
            <>
              <p className="gl-kicker">הצעד הבא שלכם</p>
              <h2>מוכנים לצמוח כמנהיגים?</h2>
              <p className="gl-download-lead">
                בחרו מסלול דיגיטלי או דברו איתנו — ונעזור לכם למצוא את הדרך המתאימה.
              </p>
              <div className="gl-academy-download-actions">
                <a className="gl-academy-hero-cta gl-academy-hero-cta--primary" href="#app">
                  לקורסי פיתוח מנהיגות
                </a>
                <button
                  type="button"
                  className="gl-academy-hero-cta gl-academy-hero-cta--ghost"
                  onClick={openTalk}
                >
                  צור קשר
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="gl-kicker">מתחילים היום</p>
              <h2>המנהיגות מחכה בכיס.</h2>
              <p className="gl-download-lead">
                הורידו את האפליקציה, בחרו את הקצב שלכם, ותראו את השינוי כבר בשבוע הראשון.
              </p>
              <StoreButtons />
            </>
          )}
        </div>
      </section>

      {showPersonalAreaPopup && (
        <div className="contact-popup-overlay" onClick={closePersonalAreaPopup}>
          <div
            className="contact-popup contact-popup--personal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="personal-area-popup-title"
          >
            <button
              type="button"
              className="close-popup"
              onClick={closePersonalAreaPopup}
              aria-label="סגירה"
            >
              ✕
            </button>
            <h2 id="personal-area-popup-title">איזור אישי</h2>
            <form className="personal-area-form" onSubmit={handlePersonalAreaSubmit}>
              <div className="form-group form-group--contact">
                <div className="phone-input-container">
                  <div className="input-shell phone-number-shell">
                    <input
                      id="personal-area-phone"
                      type="tel"
                      value={personalAreaPhone}
                      onChange={(e) => setPersonalAreaPhone(e.target.value)}
                      placeholder="מספר טלפון"
                      aria-label="מספר טלפון"
                      required
                      autoComplete="tel"
                    />
                  </div>
                  <select
                    value={personalAreaCountryCode}
                    onChange={(e) => setPersonalAreaCountryCode(e.target.value)}
                    className="country-code-select"
                    aria-label="קידומת מדינה"
                  >
                    {countryCodeOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                type="submit"
                className="verify-submit-btn personal-area-submit"
                disabled={isSubmittingPersonalArea}
              >
                {isSubmittingPersonalArea ? 'שולח...' : 'שלח'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showTalkPopup && (
        <div className="contact-popup-overlay" onClick={() => setShowTalkPopup(false)}>
          <div
            className="contact-popup contact-popup--talk"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="talk-popup-title"
          >
            <button
              type="button"
              className="close-popup"
              onClick={() => setShowTalkPopup(false)}
              aria-label="סגירה"
            >
              ✕
            </button>
            <h2 id="talk-popup-title">רוצים לדבר איתנו</h2>
            <div className="contact-inline-form contact-inline-form--popup">
            <form onSubmit={handleContactSubmit}>
              <div className="form-group form-group--name">
                <label htmlFor="contact-fullName">שם מלא</label>
                <div className="input-shell">
                  <span className="input-shell-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </span>
                  <input
                    id="contact-fullName"
                    type="text"
                    value={contactForm.fullName}
                    onChange={(e) => handleContactFormChange('fullName', e.target.value)}
                    placeholder="איך קוראים לכם?"
                    required
                  />
                </div>
              </div>
              
              <div className="form-group form-group--method">
                <label>אמצעי יצירת קשר מועדף</label>
                <div className="radio-group">
                  <label className={`contact-method-option${contactForm.contactMethod === 'phone' ? ' selected' : ''}`}>
                    <input
                      type="radio"
                      name="contactMethod"
                      value="phone"
                      checked={contactForm.contactMethod === 'phone'}
                      onChange={(e) => handleContactFormChange('contactMethod', e.target.value)}
                    />
                    <span className="method-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                    </span>
                    <span className="method-text">
                      <span className="method-title">טלפון</span>
                      <span className="method-hint">שיחה או הודעה</span>
                    </span>
                  </label>
                  <label className={`contact-method-option${contactForm.contactMethod === 'email' ? ' selected' : ''}`}>
                    <input
                      type="radio"
                      name="contactMethod"
                      value="email"
                      checked={contactForm.contactMethod === 'email'}
                      onChange={(e) => handleContactFormChange('contactMethod', e.target.value)}
                    />
                    <span className="method-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </span>
                    <span className="method-text">
                      <span className="method-title">מייל</span>
                      <span className="method-hint">תכתובת נוחה</span>
                    </span>
                  </label>
              </div>
              </div>
              
              {contactForm.contactMethod === 'phone' ? (
                <div className="form-group form-group--contact" key="phone-field">
                  <label htmlFor="contact-phone">טלפון</label>
                  <div className="phone-input-container">
                    <div className="input-shell phone-number-shell">
                      <input
                        id="contact-phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => handleContactFormChange('phone', e.target.value)}
                        placeholder="מספר טלפון"
                        required
                      />
                    </div>
                    <select
                      value={contactForm.countryCode}
                      onChange={(e) => handleContactFormChange('countryCode', e.target.value)}
                      className="country-code-select"
                      aria-label="קידומת מדינה"
                    >
                      {countryCodeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
              </div>
              ) : (
                <div className="form-group form-group--contact" key="email-field">
                  <label htmlFor="contact-email">מייל</label>
                  <div className="input-shell">
                    <span className="input-shell-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </span>
                    <input
                      id="contact-email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => handleContactFormChange('email', e.target.value)}
                      placeholder="name@example.com"
                      required
                    />
                  </div>
            </div>
              )}

              <div className="form-group form-group--times">
                <label className="times-main-label">זמנים פנויים</label>
                {contactForm.availableTimes.map((timeSlot, index) => (
                  <div key={index} className="time-slot-container" style={{ '--slot-i': index }}>
                    <div className="time-slot-header">
                      <h4 className="time-slot-title">
                        <span className="time-slot-badge">{index + 1}</span>
                        זמן פנוי
                      </h4>
                      {contactForm.availableTimes.length > 1 && (
                        <button
                          type="button"
                          className="remove-time"
                          onClick={() => removeTimeSlot(index)}
                          title="הסר זמן"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    
                    <div className="date-picker-section">
                      <label className="time-label">תאריך</label>
                      <input
                        type="date"
                        value={timeSlot.date}
                        onChange={(e) => updateTimeSlot(index, 'date', e.target.value)}
                        className="date-input"
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>

                    <div className="time-pickers-section">
                      <div className="time-picker-group">
                        <WheelTimePicker
                          value={timeSlot.fromTime}
                          onChange={(value) => updateTimeSlot(index, 'fromTime', value)}
                          label="משעה"
                          required
                        />
                      </div>
                      <div className="time-picker-group">
                        <WheelTimePicker
                          value={timeSlot.toTime}
                          onChange={(value) => updateTimeSlot(index, 'toTime', value)}
                          label="עד שעה"
                          disabled={!timeSlot.fromTime}
                          minTime={timeSlot.fromTime}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="add-time-button"
                  onClick={addTimeSlot}
                >
                  <span className="add-icon">+</span>
                  הוסף זמן נוסף
                </button>
              </div>

              <button 
                type="submit" 
                className="submit-contact"
                disabled={isSubmittingContact}
              >
                <span className="submit-contact-label">
                  {isSubmittingContact ? 'שולח...' : 'שלחו אלינו'}
                </span>
                {!isSubmittingContact && (
                  <span className="submit-contact-arrow" aria-hidden="true">←</span>
                )}
              </button>
            </form>
            </div>
          </div>
        </div>
      )}

          {/* Footer */}
          <footer className="footer gl-footer">
            <div className="gl-footer-inner">
              <div className="gl-footer-brand">
                <img src="/phone/gl-logo.png" alt="" />
                <div>
                  <strong>Great Leaders</strong>
                  <p>פיתוח מנהיגות לחיים עצמם.</p>
                </div>
              </div>
              <div className="gl-footer-actions">
                <a href="https://www.instagram.com/moshe.katan1/" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4zm5 4.2A4.8 4.8 0 1 0 16.8 12 4.8 4.8 0 0 0 12 7.2zm0 1.8a3 3 0 1 1-3 3 3 3 0 0 1 3-3zm5.15-2.55a1.05 1.05 0 1 0 1.05 1.05 1.05 1.05 0 0 0-1.05-1.05z" />
                  </svg>
                  אינסטגרם
                </a>
                <a href="https://www.linkedin.com/in/dr-moshe-katan/" target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4.7 3.3A1.8 1.8 0 1 1 2.9 5.1a1.8 1.8 0 0 1 1.8-1.8zM3.2 8.2h3.1V21H3.2zm5.1 0h3v1.7h.05c.42-.8 1.44-1.64 2.96-1.64 3.17 0 3.76 2.09 3.76 4.8V21h-3.1v-6.3c0-1.5-.03-3.43-2.09-3.43-2.09 0-2.41 1.63-2.41 3.32V21H8.3z" />
                  </svg>
                  לינקדאין
                </a>
                <button type="button" onClick={openTalk}>
                  דברו איתנו
                </button>
              </div>
            </div>
          </footer>
        </div>
      )}

      {/* Verification Screen */}
      {showVerificationScreen && (
        <div className="contact-popup-overlay verification-overlay" onClick={closeVerificationScreen}>
          <div className="contact-popup verification-popup" onClick={(e) => e.stopPropagation()}>
            <button className="close-popup" onClick={closeVerificationScreen}>✕</button>
            
            <div className="verification-header">
              <div className="verification-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#ffc915" strokeWidth="2" fill="none"/>
                  <path d="M8 12l2 2 4-4" stroke="#ffc915" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="verification-title">
                {verificationFlow === 'personal' ? 'אימות טלפון' : 'אימות קשר'}
              </h2>
              <p className="verification-subtitle">
                {verificationFlow === 'personal'
                  ? `שלחנו קוד אימות למספר ${personalAreaVerifiedPhone}`
                  : activeVerificationForm.contactMethod === 'email'
                  ? `שלחנו קוד אימות למייל ${activeVerificationForm.email}`
                  : `שלחנו קוד אימות למספר ${buildInternationalPhone(activeVerificationForm.countryCode, activeVerificationForm.phone)}`
                }
              </p>
            </div>

            <form onSubmit={handleVerificationSubmit} className="verification-form">
              <div className="verification-code-container">
                <label className="verification-label">קוד האימות</label>
                <div className="code-input-wrapper">
                  {[0, 1, 2, 3].map((index) => (
                    <input
                      key={index}
                      ref={(el) => (codeInputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      className={`code-digit${enteredCode[index] ? ' has-value' : ''}`}
                      value={enteredCode[index] || ''}
                      aria-label={`ספרה ${index + 1} מתוך 4`}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 1);
                        if (value) {
                          const newCode = enteredCode.split('');
                          newCode[index] = value;
                          const updatedCode = newCode.join('').slice(0, 4);
                          setEnteredCode(updatedCode);
                          // Auto-focus next input
                          if (index < 3 && codeInputRefs.current[index + 1]) {
                            codeInputRefs.current[index + 1].focus();
                          }
                        } else {
                          // Handle deletion
                          const newCode = enteredCode.split('');
                          newCode[index] = '';
                          setEnteredCode(newCode.join(''));
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !enteredCode[index] && index > 0) {
                          codeInputRefs.current[index - 1]?.focus();
                        } else if (e.key === 'ArrowLeft' && index > 0) {
                          e.preventDefault();
                          codeInputRefs.current[index - 1]?.focus();
                        } else if (e.key === 'ArrowRight' && index < 3) {
                          e.preventDefault();
                          codeInputRefs.current[index + 1]?.focus();
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
                        if (pastedData) {
                          setEnteredCode(pastedData);
                          const nextIndex = Math.min(index + pastedData.length, 3);
                          codeInputRefs.current[nextIndex]?.focus();
                        }
                      }}
                      maxLength="1"
                      required
                    />
                  ))}
                </div>
                <p className="code-hint">הכנס את הקוד בן 4 הספרות שקיבלת</p>
              </div>

              <button 
                type="submit" 
                className="verify-submit-btn"
                disabled={isVerifying || enteredCode.length !== 4}
              >
                {isVerifying ? (
                  <>
                    <span className="spinner"></span>
                    מאמת...
                  </>
                ) : (
                  <>
                    <span>✓</span>
                    {verificationFlow === 'personal' ? 'אמת והמשך' : 'אמת ושלח'}
                  </>
                )}
              </button>

              <button
                type="button"
                className="back-to-form-btn"
                onClick={closeVerificationScreen}
              >
                {verificationFlow === 'personal' ? '← חזור' : '← חזור לטופס'}
              </button>
            </form>
          </div>
        </div>
      )}

      {personalAreaOnboardingStep && (
        <PersonalAreaOnboarding
          step={personalAreaOnboardingStep}
          name={onboardingName}
          gender={onboardingGender}
          acceptedPrivacyPolicy={acceptedPrivacyPolicy}
          acceptedTermsOfUse={acceptedTermsOfUse}
          isSubmitting={isRegisteringPersonalArea}
          onNameChange={setOnboardingName}
          onGenderChange={setOnboardingGender}
          onAcceptedPrivacyChange={setAcceptedPrivacyPolicy}
          onAcceptedTermsChange={setAcceptedTermsOfUse}
          onContinueName={handleOnboardingNameSubmit}
          onContinueGender={handleOnboardingGenderSubmit}
          onComplete={handleOnboardingComplete}
          onClose={resetPersonalAreaOnboarding}
        />
      )}

      {successPopup && (
        <div
          className="contact-popup-overlay success-popup-overlay"
          onClick={closeSuccessPopup}
        >
          <div
            className="contact-popup success-popup"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="success-popup-title"
          >
            <button
              type="button"
              className="close-popup"
              onClick={closeSuccessPopup}
              aria-label="סגירה"
            >
              ✕
            </button>

            <div className="success-popup-icon" aria-hidden="true">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#ffc915" strokeWidth="2" fill="none"/>
                <path d="M8 12l2 2 4-4" stroke="#383c7f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <h2 id="success-popup-title" className="success-popup-title">
              היי {successPopup.name}!
            </h2>
            <p className="success-popup-message">
              הפרטים התקבלו בהצלחה. ניצור איתך קשר בהקדם האפשרי.
            </p>

            <button
              type="button"
              className="success-popup-close-btn"
              onClick={closeSuccessPopup}
            >
              סגירה
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;